import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gorunurDersler, SINAV_DERSLERI, konuAlaninda, kazanimSozleri } from '../../lib/dersler';
import { MUSTERI_PUAN_SECENEKLERI } from '../../lib/zorlukSeviye';
import PuanSecici from '../ui/PuanSecici';
import CokluSecici from '../ui/CokluSecici';
import MusteriSoruKarti from './MusteriSoruKarti';

// Bütün süzgeçler kutulu çoklu seçim (kullanıcı isteği 2026-10-26): değerler dizi, boş = tümü.
const BOS_SUZGEC = { ders: [], mufredat: [], konu: [], alt: [], kazanim: [], puan: [], gorsel: [], baglam: [], q: '' };
const MUFREDATLAR = [{ value: 'MEB_2018', label: 'MEB 2018 (2026-2027 YKS)' }, { value: 'Maarif_2024', label: 'Maarif 2024 (2028 ve sonrası)' }];
const GORSELLER = [{ value: 'var', label: 'Şekilli' }, { value: 'yok', label: 'Şekilsiz' }];
const BAGLAMLAR = [{ value: 'var', label: 'Bağlam grubu' }, { value: 'yok', label: 'Bağlam grubu olmayan' }];

// Ders pilinin öğrenme alanı kuralı (matematik/geometri aynı ağacı paylaşır — lib/dersler SINAV_DERSLERI).
const alanKurali = (ders) => [...SINAV_DERSLERI.TYT, ...SINAV_DERSLERI.AYT].find((d) => d.key === ders)?.ogrenmeAlani || null;

/**
 * Ağaç seçimi -> sunucuya gidecek kazanım kodu önekleri, DAL BAŞINA EN ÖZEL seçim: seçili konunun seçili
 * alt konusu varsa alt konular, onların seçili kazanımı varsa kazanımlar; yoksa konunun kendisi. Böylece
 * iki konu seçip yalnız birinin alt konusunu daraltmak öteki konuyu düşürmez.
 */
export function kodListesi(agac, konu, alt, kazanim) {
  const k = new Set(konu); const a = new Set(alt); const z = new Set(kazanim);
  const out = [];
  for (const kn of agac) {
    if (!k.has(kn.kod)) continue;
    const altlar = (kn.alt_konular || []).filter((x) => a.has(x.kod));
    if (!altlar.length) { out.push(kn.kod); continue; }
    for (const al of altlar) {
      const kz = (al.kazanimlar || []).filter((x) => z.has(x.kod));
      if (kz.length) out.push(...kz.map((x) => x.kod)); else out.push(al.kod);
    }
  }
  return out;
}

/** Üst düzeyden düşen seçim alt düzeyde kalmasın (konu yeniden seçilince eski işaretler hortlamasın). */
function buda(agac, konu, alt, kazanim) {
  const altlar = agac.filter((k) => konu.includes(k.kod)).flatMap((k) => k.alt_konular || []);
  const altY = alt.filter((x) => altlar.some((a) => a.kod === x));
  const kzlar = altlar.filter((a) => altY.includes(a.kod)).flatMap((a) => a.kazanimlar || []);
  return { konu, alt: altY, kazanim: kazanim.filter((x) => kzlar.some((z) => z.kod === x)) };
}

/**
 * Müşteri soru listesi (2026-10-26): süzgeç + 20'lik sayfa + "Daha fazla" + ızgara kartlar. Kullanıcı
 * isteği gereği HİÇBİR soru sayısı gösterilmez — sunucu da toplam döndürmez (`daha_var` yalnız).
 */
export default function MusteriSorular({ api, sepet, onSepet, onTopluEkle, mesgul, izinliDersler }) {
  const [s, setS] = useState(BOS_SUZGEC);
  const [araMetni, setAraMetni] = useState('');
  const [agac, setAgac] = useState([]);
  const [liste, setListe] = useState([]);
  const [sayfa, setSayfa] = useState(1);
  const [dahaVar, setDahaVar] = useState(false);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const istekNo = useRef(0);
  const mufredat = s.mufredat.length === 1 ? s.mufredat[0] : '';   // ikisi ya da hiçbiri = tümü
  const tekDers = s.ders.length === 1 ? s.ders[0] : '';
  const soz = kazanimSozleri(mufredat);
  const set = (parca) => setS((o) => ({ ...o, ...parca }));
  const dersSecenekleri = ['TYT', 'AYT'].flatMap((sinav) => gorunurDersler(izinliDersler)
    .filter((d) => d.value && d.value.startsWith(`${sinav.toLowerCase()}_`))
    .map((d) => ({ value: d.value, label: d.label, grup: sinav })));

  // Müfredat ağacı yalnız TEK ders seçiliyken (çok derste kod numaraları çakışır). "Tümü" müfredatında MEB ağacı.
  useEffect(() => {
    if (!tekDers) { setAgac([]); return undefined; }
    let iptal = false;
    api.kazanim(tekDers, mufredat === 'Maarif_2024' ? 'Maarif_2024' : '')
      .then((d) => { if (!iptal) setAgac((d.konular || []).filter((k) => konuAlaninda(k, alanKurali(tekDers), mufredat))); })
      .catch(() => { if (!iptal) setAgac([]); });
    return () => { iptal = true; };
  }, [api, tekDers, mufredat]);

  // Kod listesi dizgiyle anahtarlanır: her çizimde yeni dizi sorguyu ve listeyi yeniden tetiklerdi.
  const kazanimMetni = kodListesi(agac, s.konu, s.alt, s.kazanim).join(',');
  const sorgu = useMemo(() => ({
    ders: s.ders, mufredat: s.mufredat, kazanim: kazanimMetni ? kazanimMetni.split(',') : [],
    puan: s.puan, gorsel: s.gorsel, baglam: s.baglam, q: s.q,
  }), [s.ders, s.mufredat, kazanimMetni, s.puan, s.gorsel, s.baglam, s.q]);

  const getir = useCallback(async (n, ekle) => {
    const no = ++istekNo.current;
    setYukleniyor(true); setHata('');
    try {
      const d = await api.sorular(sorgu, n);
      if (no !== istekNo.current) return;
      setListe((o) => {
        if (!ekle) return d.sorular || [];
        const var_ = new Set(o.map((x) => x.soru_no));
        return [...o, ...(d.sorular || []).filter((x) => !var_.has(x.soru_no))];
      });
      setSayfa(n);
      setDahaVar(Boolean(d.daha_var));
    } catch (e) {
      if (no !== istekNo.current) return;
      setHata(e.message || 'Sorular alınamadı');
      if (!ekle) { setListe([]); setDahaVar(false); }
    }
    if (no === istekNo.current) setYukleniyor(false);
  }, [api, sorgu]);

  // Süzgeç değişince liste baştan (eski sayfaların yanıtı istekNo ile düşer).
  useEffect(() => { getir(1, false); }, [getir]);

  // Ağaç seçenekleri: alt konu = seçili konuların alt konuları, kazanım = seçili alt konuların kazanımları.
  const seciliKonular = agac.filter((k) => s.konu.includes(k.kod));
  const altSecenekleri = seciliKonular.flatMap((k) => (k.alt_konular || [])
    .map((a) => ({ value: a.kod, label: `${a.kod} ${a.ad}`, grup: seciliKonular.length > 1 ? `${k.kod} ${k.ad}` : undefined })));
  const seciliAltlar = seciliKonular.flatMap((k) => (k.alt_konular || []).filter((a) => s.alt.includes(a.kod)));
  const kazanimSecenekleri = seciliAltlar.flatMap((a) => (a.kazanimlar || [])
    .map((z) => ({ value: z.kod, label: `${z.kod} ${z.ad}`, grup: seciliAltlar.length > 1 ? `${a.kod} ${a.ad}` : undefined })));

  const eklenebilir = liste.filter((x) => !sepet.has(x.soru_no) && !x.gorsel_hata).map((x) => x.soru_no);
  const suzgecVar = JSON.stringify({ ...s, q: s.q.trim() }) !== JSON.stringify(BOS_SUZGEC);

  return (
    <div className="space-y-3">
      <div data-musteri-suzgec className="bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <CokluSecici etiket="Ders" deger={s.ders} secenekler={dersSecenekleri}
          onDegis={(ders) => set({ ders, konu: [], alt: [], kazanim: [] })} />
        <CokluSecici etiket="Müfredat" deger={s.mufredat} secenekler={MUFREDATLAR}
          onDegis={(m) => set({ mufredat: m, konu: [], alt: [], kazanim: [] })} />
        {tekDers && agac.length > 0 && (
          <CokluSecici etiket="Konu" deger={s.konu} secenekler={agac.map((k) => ({ value: k.kod, label: `${k.kod} ${k.ad}` }))}
            onDegis={(konu) => set(buda(agac, konu, s.alt, s.kazanim))} />
        )}
        {altSecenekleri.length > 0 && (
          <CokluSecici etiket="Alt konu" deger={s.alt} secenekler={altSecenekleri}
            onDegis={(alt) => set(buda(agac, s.konu, alt, s.kazanim))} />
        )}
        {kazanimSecenekleri.length > 0 && (
          <CokluSecici etiket={soz.Ad} deger={s.kazanim} secenekler={kazanimSecenekleri} onDegis={(kazanim) => set({ kazanim })} />
        )}
        <PuanSecici deger={s.puan} onDegis={(l) => set({ puan: l })} secenekler={MUSTERI_PUAN_SECENEKLERI}
          ariaLabel="Puan" title="Öğretmen zorluk puanı — birden çok puan seçilebilir" />
        <CokluSecici etiket="Görsel" deger={s.gorsel} secenekler={GORSELLER} onDegis={(gorsel) => set({ gorsel })} />
        <CokluSecici etiket="Bağlam grubu" deger={s.baglam} secenekler={BAGLAMLAR} onDegis={(baglam) => set({ baglam })} />
        <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); set({ q: araMetni }); }}>
          <input aria-label="Metinde ara" value={araMetni} onChange={(e) => setAraMetni(e.target.value)} placeholder="Metinde ara (en az 2 harf)"
            className="px-2 py-1.5 rounded border border-gray-300 text-xs w-48" />
          <button type="submit" className="px-2 py-1.5 rounded bg-gray-100 text-xs font-semibold text-gray-700 hover:bg-gray-200">Ara</button>
        </form>
        {suzgecVar && (
          <button type="button" data-suzgec-temizle onClick={() => { setS(BOS_SUZGEC); setAraMetni(''); }}
            className="px-2 py-1.5 rounded text-xs text-gray-500 hover:bg-gray-100">✕ Süzgeçleri temizle</button>
        )}
      </div>

      {hata && <div className="p-3 rounded-lg border border-red-300 bg-red-50 text-sm text-red-700">{hata}</div>}

      {eklenebilir.length > 1 && (
        <div className="flex justify-end">
          <button type="button" data-toplu-ekle disabled={mesgul} onClick={() => onTopluEkle(eklenebilir)}
            className="px-3 py-1.5 rounded bg-blue-50 border border-blue-200 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50">
            Listedekilerin hepsini sepete ekle
          </button>
        </div>
      )}

      <div data-soru-izgara className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
        {liste.map((x) => (
          <MusteriSoruKarti key={x.soru_no} soru={x} api={api} sepette={sepet.has(x.soru_no)} onSepet={onSepet} mesgul={mesgul} />
        ))}
      </div>

      {!yukleniyor && !hata && liste.length === 0 && (
        <div data-bos-liste className="p-8 text-center text-sm text-gray-500 bg-white rounded-xl border border-gray-200">
          Bu süzgeçlere uyan soru yok. Süzgeçleri genişletmeyi deneyin.
        </div>
      )}
      {yukleniyor && <div className="py-4 text-center text-sm text-gray-400">Sorular yükleniyor…</div>}
      {dahaVar && !yukleniyor && (
        <div className="text-center">
          <button type="button" data-daha-fazla onClick={() => getir(sayfa + 1, true)}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50">
            Daha fazla soru
          </button>
        </div>
      )}
    </div>
  );
}
